import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import color from "../../../constants/color";
import styles from "./style";
import CustomPagination from "../../../components/CustomPagination";
import { ReactComponent as SearchIcon } from "../../../assets/images/searchIcon.svg";
import Modal from "../../../components/Modal";
import { formatDate } from "../../../utils/DateFormat";

const SubAdminListPage = ({
  subAdmins = [],
  page = 1,
  total = 0,
  setPage = () => { },
  setRowsPerPage = () => { },
  rowsPerPage = 10,
  searchText = "",
  onSearchChange = () => { },
  loading = false,
  deleteModalOpen = false,
  deleteTargetSubAdmin = null,
  onDeleteSubAdmin = () => { },
  onCloseDeleteModal = () => { },
  onConfirmDeleteModal = () => { },
  deleteLoading = false,
}) => {
  const navigate = useNavigate();

  const handleAddNewSubAdmin = () => {
    navigate("/dashboard/sub-admin/create");
  };

  const handleViewSubAdmin = (subAdmin) => {
    const subAdminId = subAdmin?._id || subAdmin?.id;
    if (subAdminId) {
      navigate(`/dashboard/sub-admin/view/${subAdminId}`);
    }
  };

  const handleEditSubAdmin = (subAdmin) => {
    const subAdminId = subAdmin?._id || subAdmin?.id;
    if (subAdminId) {
      navigate(`/dashboard/sub-admin/edit/${subAdminId}`);
    }
  };

  const formatCountries = (countries = []) => {
    if (!countries || countries.length === 0) return "N/A";
    if (countries.length <= 2) {
      return countries.join(", ");
    }
    return `${countries.slice(0, 2).join(", ")} +${countries.length - 2}`;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>Sub Admin Management</Typography>
        <Button
          variant="contained"
          onClick={handleAddNewSubAdmin}
          sx={styles.addButton}
        >
          + Add Sub Admin
        </Button>
      </Box>

      {/* Table Section */}
      <Box sx={styles.tableSection}>
        <Box sx={styles.tableHeader}>
          <Box sx={styles.tableHeaderLeft}>
            <Box sx={styles.searchBox}>
              <IconButton type="button" sx={styles.searchIconButton}>
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={styles.searchInput}
                placeholder="Search by name or email"
                value={searchText}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Box>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeaderCell}>Full Name</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Email</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Phone</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Assigned Countries</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Created Date</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Status</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={styles.emptyCell}>
                    <Typography>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : subAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={styles.emptyCell}>
                    <Typography>No sub admins found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subAdmins.map((subAdmin, index) => (
                  <TableRow
                    key={subAdmin._id || subAdmin.id || index}
                    sx={{
                      ...styles.tableRow,
                      ...(subAdmin.status === "inactive" ? styles.inactiveRow : {}),
                    }}
                  >
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      {`${subAdmin.admin?.firstName || ""} ${subAdmin.admin?.lastName || ""
                        }`.trim() || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      {subAdmin.email || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      {subAdmin?.admin?.phone || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      {formatCountries(subAdmin.assignedCountries)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      {formatDate(subAdmin.createdAt)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(subAdmin.status === "inactive"
                          ? styles.inactiveTableCell
                          : {}),
                      }}
                    >
                      <Chip
                        label={subAdmin.admin?.isActive ? "Active" : "Inactive"}
                        sx={{
                          ...styles.statusChip,
                          backgroundColor: subAdmin.admin?.isActive
                            ? color.lightGreen
                            : "#FCEBEB",

                          color: subAdmin.admin?.isActive
                            ? color.primary
                            : "#C62828",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={() => handleViewSubAdmin(subAdmin)}
                            sx={styles.viewButton}
                            size="small"
                          >
                            <RemoveRedEyeOutlinedIcon sx={styles.viewIcon} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEditSubAdmin(subAdmin)}
                            sx={styles.editButton}
                            size="small"
                          >
                            <EditIcon sx={styles.editIcon} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <DeleteOutlineIcon
                            sx={{
                              color: "#ff0000",
                              cursor: "pointer",
                              mt: "4px",
                            }}
                            fontSize="small"
                            onClick={() => onDeleteSubAdmin(subAdmin)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CustomPagination
          pageCount={total}
          page={page}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          totalItems={subAdmins.length}
        />
      </Box>

      <Modal
        open={deleteModalOpen}
        setOpen={onCloseDeleteModal}
        title="Delete Sub Admin"
        deletetText="Delete"
        cancelText="Cancel"
        handleDelete={onConfirmDeleteModal}
        loading={deleteLoading}
      >
        <Box>
          <Typography>
            Are you sure you want to delete {deleteTargetSubAdmin?.firstName}{" "}
            {deleteTargetSubAdmin?.lastName}?
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default SubAdminListPage;
